// Copyright: IQGeo Limited 2010-2019
import myw from 'myWorld/base/core';
import './datasource';
import 'myWorld/layers/kmlLayer';
import 'myWorld/layers/kmzLayer';

/**
 *  @class Datasource to provide visualization of KML files
 *  @name KmlDatasource
 */
myw.KmlDatasource = myw.Datasource.extend('KmlDatasource', {
    defaultFeatureModel: myw.MyWorldFeature,
    statics: {
        layerDefFields: [
            {
                name: 'isKmz',
                type: 'boolean',
                default: false,
                onChange: 'rebuildForm'
            },
            {
                name: 'relativeUrl',
                type: 'string',
                viewClass: 'StringWithTestView',
                args: {
                    testUrlField: null
                },
                condition(def) {
                    return !def.spec.isKmz;
                }
            },
            {
                name: 'kmzFile',
                type: 'string',
                condition(def) {
                    return def.spec.isKmz;
                }
            },
            {
                name: 'fileInKmz',
                type: 'string',
                viewClass: 'StringWithTestView',
                args: {
                    testUrlField: null
                },
                condition(def) {
                    return def.spec.isKmz;
                }
            }
        ],
        specFields: [{ name: 'baseUrl', type: 'string', size: 'long' }]
    },

    /**
     * Instantiates a leaflet layer from a layer definition
     * @param  {layerDefinition} layerDef
     * @return {Promise<ILayer>} a L.KML instance
     */
    async createLayer(layerDef) {
        let clazz = null;
        let url = null;
        if (layerDef.isKmz) {
            url = await this.system.getUrlForKmz(layerDef);
            clazz = myw.KmzLayer;
        } else {
            url = await this.system.getUrlForKml(layerDef);
            clazz = myw.KmlLayer;
        }
        if (layerDef.owner) url += `?owner=${layerDef.owner}`;
        const layer = new clazz(url, layerDef, this);
        this.isInvalid = false;
        layer.on('invalid', () => {
            this.isInvalid = true;
        });
        return layer;
    },

    /**
     * Returns the url to test the layer def
     * @param  {string} relativeUrl String to append to the base URL
     * @return {string}
     */
    getLayerURL(def) {
        if (def.spec.isKmz) {
            return this._formatKmzUrl(def.spec.kmzFile, def.spec.fileInKmz);
        } else {
            let baseUrl = def.baseUrl;
            if (baseUrl.slice(-1) !== '/') baseUrl += '/'; //Make sure there is a trailing '/' with the url

            return baseUrl + def.spec.relativeUrl;
        }
    },

    _formatKmzUrl(kmzFile, fileInKmz) {
        let baseUrl = this.options.baseUrl;
        if (baseUrl.slice(-1) !== '/') baseUrl += '/';
        if (kmzFile.slice(-1) === '/') kmzFile = kmzFile.slice(0, -1); //Make sure there is no trailing '/' with the url
        if (fileInKmz.slice(-1) === '/') fileInKmz = fileInKmz.slice(0, -1); //Make sure there is no trailing '/' with the url
        return `system/kmz/${btoa(baseUrl + kmzFile)}/${btoa(fileInKmz)}`;
    },

    /**
     * Tests the datasource url for the layers config page
     * @param  {string}          relativeUrl   Relative url for the layerDef spec
     * @return {Promise}                       When resolved returns an object with a success boolean and message to alert the user of any errors
     */
    testLayerURL(spec) {
        if (spec.isKmz) {
            if (spec.kmzFile.length === 0) throw new Error('Please enter a KMZ file to use');
            if (spec.fileInKmz.length === 0)
                throw new Error('Please enter a file inside the KMZ file to use');
            return this.system.server
                .ajax({
                    async: true,
                    type: 'GET',
                    url: this._formatKmzUrl(spec.kmzFile, spec.fileInKmz)
                })
                .then(res => 'success');
        } else {
            if (spec.relativeUrl.length === 0) throw new Error('Please enter a URL');

            const options = {
                urlFieldName: 'baseUrl',
                relativeUrl: spec.relativeUrl,
                format: 'xml'
            };

            return this.tunnelRequest({}, options).then(() => 'success');
        }
    }
});

myw.datasourceTypes['kml'] = myw.KmlDatasource;

export default myw.KmlDatasource;
